import { Injectable, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DataService } from './../../services/data-services/data.service';
import { IBalanceItem } from './../../models/balance-item';
import { ImportTypeEnum } from './../../models/import-type.enum';
import { FieldMapping } from './../../models/mapped-field-item';
import { ITransaction } from './../../models/transaction';
import { TransactionTypeEnum } from './../../models/transaction-type.enum';
import { ImportMapping } from './../../models/import-mapping';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TransactionImportService
  extends DataService<any>
  implements OnInit
{
  ngOnInit(): void {}

  private formatImportTransactionsByType = {
    [ImportTypeEnum.CashApp]: this.formatCashApp.bind(this), //may need only 1
    [ImportTypeEnum.PayPal]: this.formatPayPal.bind(this), // may need only 1
    [ImportTypeEnum.Other]: this.formatOther.bind(this),
  };

  getImportMappings(): Observable<ImportMapping[]> {
    const url = `${this.urlService.buildUrl(
      'transactions',
      this.organization.id ||
        this.organization['_id'] ||
        this.organization?.organizationId
    )}/import-mappings`;

    let body = {
      organizationId: this.organization.id,
    };
    return this.http.get<ImportMapping[]>(`${url}`).pipe(
      map((res: Array<ImportMapping>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<ImportMapping>);
      }),
      catchError(this.errorMgmt)
    );
  }

  addImportMappings(mapping: ImportMapping): Observable<ImportMapping> {
    const url = `${this.urlService.buildUrl(
      'transactions',
      this.organization.id ||
        this.organization['_id'] ||
        this.organization?.organizationId
    )}/import-mappings`;

    let body = {
      // organizationId: this.organization.id,
      mapping,
    };
    return this.http.post<any>(`${url}`, mapping);
  }

  editImportMappings(mapping: ImportMapping): Observable<ImportMapping> {
    const url = `${this.urlService.buildUrl(
      'transactions',
      this.organization.id ||
        this.organization['_id'] ||
        this.organization?.organizationId
    )}/import-mappings/${mapping.id}`;

    return this.http.patch<ImportMapping>(`${url}`, mapping).pipe(
      map((mapping: ImportMapping) => {
        this.setId(mapping);
        return mapping;
      }),
      catchError(this.errorMgmt)
    );
  }

  formatRecords(
    records: any[],
    importType: ImportTypeEnum
  ): Observable<ITransaction[]> {
    return this.formatImportTransactionsByType[ImportTypeEnum[importType]](
      records
    );
  }

  private isExpense(amountStr): boolean {
    // Check if it contains a negative sign or is wrapped in parentheses
    return /^-|\(.*\)/.test(amountStr);
  }
  private formatCashApp(records: any[]): Observable<ITransaction[]> {
    const formattedTransactions = records.map((record) => {
      // return record;
      const amountString = record.Amount.replace(/[^0-9\.]+/g, '');
      const isCredit = !this.isExpense(record.Amount); //(amountString);

      return {
        creditId: isCredit ? record['accountId'] : undefined,
        debitId: !isCredit ? record['accountId'] : undefined,
        amount: Number(record.Amount.replace(/[^0-9\.]+/g, '')),
        transactionDate: record['Date'],
        // type: record.Status === 'PAYMENT DEPOSITED' ? 'deposit' : 'expense',
        transactionType: isCredit ? 'deposit' : 'expense',
        title:
          record.Notes && record.Notes.length > 0
            ? `${record.Notes} (${record['Name of sender/receiver']})`
            : `${record.Status}`,
        friendlyId: record['friendlyId'],
        account: [
          {
            itemId: record['accountId'],
            balanceType: isCredit ? 'account_credit' : 'account_debit', //his.getBalanceType(record.Status),
          } as IBalanceItem,
        ],
        // createdBy: record.createdBy,
        // organizationId: record.organizationId,
        // attributes: record['attributes'],
        // status: 'Completed',
        sourceTransactionId: record['Transaction ID'],
        note: `CashApp Transaction Id:  ${record['Transaction ID']}`,
        id: record['Transaction ID'],
      } as ITransaction;
    });

    return of(formattedTransactions);
  }

  generateUniqueId = () => {
    return '_' + Math.random().toString(36).substring(2, 9);
  };

  private getBalanceType(status: any): string {
    if (status === 'PAYMENT SENT' || status === 'TRANSFER SENT')
      return 'account_debit';
    else if (status === 'PAYMENT DEPOSITED' || status === 'TRANSFER DEPOSITED')
      return 'account_credit';
    else return 'account_credit';
  }

  private formatPayPal(records: any[]): Observable<ITransaction[]> {
    const balanceAffect = ['Credit', 'Debit'];

    const filteredRecords = records.filter((a) =>
      balanceAffect.includes(a['Balance Impact'])
    );

    const formattedTransactions = filteredRecords.map((record) => {
      // return record;
      let title = this.getPaypalTitle(record);
      const amount = parseFloat(record['Gross']);
      const net = parseFloat(record['Net']);
      const isCredit = record['Balance Impact'] === 'Credit';
      const isDebit = record['Balance Impact'] === 'Debit';
      const type = isCredit ? 'deposit' : 'expense';
      return {
        from: record['From Email Address'],
        to: record['To Email Address'],
        creditId: isCredit ? record['accountId'] : undefined,
        debitId: isDebit ? record['accountId'] : undefined,
        fees: {
          paypalFee: Number(record['Fee'].replace(/[^0-9\.]+/g, '')),
          total: Number(record['Fee'].replace(/[^0-9\.]+/g, '')),
        },
        amount: Math.abs(net),
        total: Math.abs(amount),
        transactionDate: `${record['Date']} ${record['Time']} ${record['TimeZone']}`,
        // type: record.Status === 'PAYMENT DEPOSITED' ? 'deposit' : 'expense',
        transactionType: type,
        title: title,
        friendlyId: record['friendlyId'],
        // createdBy: record.createdBy,
        // organizationId: record.organizationId,
        // attributes: record['attributes'],
        // status: 'Completed',
        sourceTransactionId: record['Transaction ID'],
        note: `Name: ${record['Name']} 
               Phone: ${record['Contact Phone Number']}
               Email: ${
                 isCredit
                   ? record['From Email Address']
                   : record['To Email Address']
               }
              `,
        id: this.generateUniqueId(),
      } as ITransaction;
    });

    return of(formattedTransactions);
  }

  private getPaypalTitle(record: any): string {
    let title =
      record['Subject'] && record['Subject'].length > 0
        ? record['Subject']
        : record['Note'];

    if (!(title && title.length > 0)) {
      title = record['Type'];
    }
    return `${title} - (${record['Name']}) `;
  }

  private formatOther(records: any[]): Observable<ITransaction[]> {
    return of(records);
  }

  private setTraxTypeAndAmount(trax: ITransaction) {
    const isDebit =
      trax.amount.toString().includes('-') ||
      (trax.amount.toString().includes('(') &&
        trax.amount.toString().includes(')'));

    trax.transactionType = isDebit
      ? TransactionTypeEnum.Expense
      : TransactionTypeEnum.Deposit;
    // trax.amount = Number(trax.amount.toString().replace(/[^0-9\.]+/g, ''));

    if (trax.transactionType === TransactionTypeEnum.Expense) {
      trax.debitId = trax['accountId'];
    } else {
      trax.creditId = trax['accountId'];
    }
  }

  manualFormat(
    records: any[],
    mappings: FieldMapping[]
  ): Observable<ITransaction[]> {
    const formattedTransactions = records.map((record) => {
      const trax = {} as ITransaction;

      mappings.forEach((mapping) => {
        trax[mapping.mapTo] = record[mapping.header];

        if (mapping && mapping.mapTo === 'amount') {
          if (
            mapping.balanceType === 'all' ||
            mapping.balanceType === undefined
          ) {
            this.setTraxTypeAndAmount(trax);
            if (trax.transactionType === TransactionTypeEnum.Expense)
              trax.debitId = record['accountId'];
            if (trax.transactionType === TransactionTypeEnum.Deposit)
              trax.creditId = record['accountId'];
          }

          if (mapping.balanceType === 'debit') {
            trax.transactionType = TransactionTypeEnum.Expense;
            trax.debitId = record['accountId'];
          }

          if (mapping.balanceType === 'credit') {
            trax.transactionType = TransactionTypeEnum.Deposit;
            trax.creditId = record['accountId'];
          }
          trax.amount = Number(
            trax['amount'].toString().replace(/[^0-9\.]+/g, '')
          );
        }

        if (mapping.mapTo === 'fees') {
          if (trax && trax.fees) {
            trax.fees = Number(
              trax['fees'].toString().replace(/[^0-9\.]+/g, '')
            );
          }
        }

        if (mapping.mapTo === 'netAmount') {
          if (trax && trax['netAmount']) {
            trax['netAmount'] = Number(
              trax['netAmount'].toString().replace(/[^0-9\.]+/g, '')
            );
          }
        }

        trax.friendlyId = record['friendlyId'];
        trax.attributes = record['attributes'];
        trax.createdBy = record['createdBy'];
        trax.account = [
          {
            itemId: record['accountId'],
            balanceType:
              trax.transactionType === TransactionTypeEnum.Deposit
                ? 'account_credit'
                : 'account_debit',
          } as IBalanceItem,
        ];
        trax.organizationId = record['organizationId'];
      });
      trax.id = this.generateUniqueId();
      return trax;
    });
    return of(formattedTransactions);
  }
}
